import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartCard from '../components/charts/ChartCard';
import ChartTooltip from '../components/charts/ChartTooltip';
import { FilterGroup, FilterSelect } from '../components/charts/FilterControls';
import {
  CHART_COLORS,
  CHART_MARGIN,
  SERIES_COLORS,
  Y_AXIS_WIDTH,
  formatDayLabel,
  formatUsd,
  formatUsdAxis,
} from '../components/charts/chartUtils';
import DashboardLayout from '../components/DashboardLayout';
import { useProject } from '../contexts/ProjectContext';
import { dashboardService } from '../services/dashboard';

const FACETS = [
  { id: 'project', label: 'Project' },
  { id: 'api_key', label: 'API key' },
  { id: 'model', label: 'Model' },
];

const TIMEFRAMES = [
  { id: '7', label: '7d' },
  { id: '30', label: '30d' },
  { id: '90', label: '90d' },
];

export default function BillingPage() {
  const { projects, setSelectedProject } = useProject();
  const [searchParams] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [filters, setFilters] = useState({
    project: '',
    api_key: '',
    model: '',
    facet: 'project',
    days: '30',
  });
  const [autoTopUp, setAutoTopUp] = useState({ enabled: false, threshold_usd: 5, amount_usd: 100 });
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState(
    searchParams.get('success') ? 'Credits added successfully!' : '',
  );

  const loadBilling = () => {
    setLoadError('');
    dashboardService.billing(filters, (res) => {
      setBilling(res.data);
      if (res.data?.auto_topup) setAutoTopUp(res.data.auto_topup);
    }, () => setLoadError('Could not load billing data. Try refreshing or contact support.'));
  };

  useEffect(() => { loadBilling(); }, [filters]);

  const costChart = useMemo(
    () => (billing?.cost_daily || []).map((row) => ({
      label: formatDayLabel(row.day),
      optimized: Number(row.optimized || 0),
      passthrough: Number(row.passthrough || 0),
      evals: Number(row.evals || 0),
    })),
    [billing],
  );

  const savingsChart = useMemo(
    () => (billing?.savings_daily || []).map((row) => ({
      label: formatDayLabel(row.day),
      routed_savings: Number(row.routed_savings || 0),
      cached_savings: Number(row.cached_savings || 0),
    })),
    [billing],
  );

  const totalCost = useMemo(
    () => costChart.reduce((sum, row) => sum + row.optimized + row.passthrough + row.evals, 0),
    [costChart],
  );

  const totalSavings = useMemo(
    () => savingsChart.reduce((sum, row) => sum + row.routed_savings + row.cached_savings, 0),
    [savingsChart],
  );

  const facetChart = useMemo(
    () => (billing?.facet_series || []).map((row) => ({
      label: row.label.length > 20 ? `${row.label.slice(0, 18)}…` : row.label,
      cost: row.cost_usd,
      savings: row.savings_usd,
    })),
    [billing],
  );

  const facetCostTimeseries = useMemo(() => {
    const labels = billing?.facet_cost_daily?.labels || [];
    const series = billing?.facet_cost_daily?.series || [];
    return {
      labels,
      chartData: series.map((row) => ({
        label: formatDayLabel(row.day),
        ...Object.fromEntries(labels.map((name) => [name, row[name] ?? 0])),
      })),
    };
  }, [billing]);

  const facetLabel = FACETS.find((f) => f.id === filters.facet)?.label?.toLowerCase() || filters.facet;

  const feeRate = billing?.credit_purchase_fee_rate ?? 0.05;
  const options = billing?.filter_options || {};

  return (
    <DashboardLayout title="Credits">
      {loadError && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-600 text-sm">
          {loadError}
        </div>
      )}

      {message && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 text-sm">
          {message}
        </div>
      )}

      <p className="text-sm opacity-50 -mt-4 mb-6">{billing?.email}</p>

      {/* Balance */}
      <div className="rounded-2xl border border-theme-primary/25 bg-gradient-to-br from-theme-primary/10 via-theme-main to-theme-main p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-50 mb-1">Current balance</div>
            <div className="text-4xl font-bold tabular-nums text-theme-primary">
              ${billing?.prepaid_balance_usd?.toFixed(2) ?? '0.00'}
            </div>
          </div>
          <button
            type="button"
            onClick={loadBilling}
            className="text-xs text-theme-text opacity-50 hover:opacity-100 transition-opacity"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Link
          to="/billing/add-credits/"
          className="group rounded-2xl border border-theme-secondary bg-theme-main p-5 hover:border-theme-primary/50 transition-colors"
        >
          <h2 className="font-semibold mb-1">Buy credits</h2>
          <p className="text-sm opacity-60 mb-4">Add prepaid credits to your account.</p>
          <span className="text-sm font-medium text-theme-primary group-hover:underline">Add credits →</span>
        </Link>

        <Link
          to="/billing/auto-top-up/"
          className="group rounded-2xl border border-theme-secondary bg-theme-main p-5 hover:border-theme-primary/50 transition-colors"
        >
          <h2 className="font-semibold mb-1">Auto top-up</h2>
          <p className="text-sm opacity-70 leading-relaxed mb-4">
            {autoTopUp.enabled ? (
              <>
                Enabled: adds <span className="font-semibold text-theme-text">${autoTopUp.amount_usd}</span>
                {' '}when balance drops below{' '}
                <span className="font-semibold text-theme-text">${autoTopUp.threshold_usd}</span>.
              </>
            ) : (
              'Disabled. Set a threshold and amount to recharge automatically.'
            )}
          </p>
          <span className="text-sm font-medium text-theme-primary group-hover:underline">Manage →</span>
        </Link>
      </div>

      <Outlet context={{ feeRate, autoTopUp, loadBilling, setMessage }} />

      {/* Usage charts */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex flex-wrap items-center gap-4">
          <FilterSelect
            label="Project"
            value={filters.project}
            allLabel="All"
            onChange={(v) => {
              setFilters((f) => ({ ...f, project: v, api_key: '' }));
              if (v) setSelectedProject(v);
            }}
            options={projects.map((p) => ({ value: p.hash, label: p.name }))}
          />
          <FilterSelect
            label="API key"
            value={filters.api_key}
            onChange={(v) => setFilters((f) => ({ ...f, api_key: v }))}
            options={(options.keys || []).map((k) => ({ value: k.hash, label: k.name }))}
          />
          <FilterSelect
            label="Model"
            value={filters.model}
            onChange={(v) => setFilters((f) => ({ ...f, model: v }))}
            options={(options.models || []).map((m) => ({ value: m, label: m }))}
          />
        </div>
        <FilterGroup
          label="Period"
          options={TIMEFRAMES}
          value={filters.days}
          onChange={(days) => setFilters((f) => ({ ...f, days }))}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <ChartCard
          title={(
            <>
              Cost{' '}
              <span className="opacity-60 font-normal tabular-nums">{formatUsd(totalCost)}</span>
            </>
          )}
          subtitle="Optimized, passthrough, and eval spend"
          tall
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={costChart} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
              <YAxis tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.usd} opacity={0.5} />
              <Tooltip content={<ChartTooltip valueFormatter={formatUsd} showTotal />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="optimized" stackId="c" fill={CHART_COLORS.routed} name="Optimized" />
              <Bar dataKey="passthrough" stackId="c" fill={CHART_COLORS.passthrough} name="Passthrough" />
              <Bar dataKey="evals" stackId="c" fill={CHART_COLORS.eval} name="Evals" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title={(
            <>
              Savings{' '}
              <span className="opacity-60 font-normal tabular-nums">{formatUsd(totalSavings)}</span>
            </>
          )}
          subtitle="Routed + cached savings"
          tall
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={savingsChart} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
              <YAxis tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.usd} opacity={0.5} />
              <Tooltip content={<ChartTooltip valueFormatter={formatUsd} />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="routed_savings" stackId="s" fill={CHART_COLORS.routed} name="Routed" />
              <Bar dataKey="cached_savings" stackId="s" fill={CHART_COLORS.cached} name="Cached" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="mb-8">
        <div className="flex flex-wrap items-center gap-4 mb-5">
          <FilterGroup
            label="Group by"
            options={FACETS}
            value={filters.facet}
            onChange={(facet) => setFilters((f) => ({ ...f, facet }))}
          />
        </div>

        {(facetChart.length > 0 || facetCostTimeseries.chartData.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {facetChart.length > 0 && (
              <ChartCard
                title={`Cost & savings by ${facetLabel}`}
                subtitle={`Last ${filters.days} days`}
                tall
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={facetChart} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} />
                    <YAxis tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.usd} opacity={0.5} />
                    <Tooltip content={<ChartTooltip valueFormatter={formatUsd} />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="cost" fill={CHART_COLORS.accent} name="Cost" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="savings" fill={CHART_COLORS.cached} name="Savings" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {facetCostTimeseries.chartData.length > 0 && facetCostTimeseries.labels.length > 0 && (
              <ChartCard
                title={`Cost by ${facetLabel} over time`}
                subtitle={`Top ${facetCostTimeseries.labels.length} by spend`}
                tall
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={facetCostTimeseries.chartData} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-secondary-bg)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} opacity={0.5} interval="preserveStartEnd" />
                    <YAxis tickFormatter={formatUsdAxis} tick={{ fontSize: 10 }} width={Y_AXIS_WIDTH.usd} opacity={0.5} />
                    <Tooltip content={<ChartTooltip valueFormatter={formatUsd} />} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {facetCostTimeseries.labels.map((name, index) => (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        dot={false}
                        strokeWidth={2}
                        stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                        name={name.length > 20 ? `${name.slice(0, 18)}…` : name}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        )}
      </div>

      {/* Transaction history */}
      <div className="rounded-2xl border border-theme-secondary overflow-hidden">
        <div className="px-5 py-4 border-b border-theme-secondary flex items-center justify-between">
          <h2 className="font-semibold">Recent Transactions</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-theme-secondary text-left text-xs uppercase tracking-wide opacity-50">
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Credits</th>
              <th className="px-5 py-3">Charged</th>
              <th className="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {(billing?.transactions || []).length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center opacity-50">No transactions yet</td>
              </tr>
            ) : (
              billing.transactions.map((t) => (
                <tr key={t.id} className="border-b border-theme-secondary/50 hover:bg-theme-secondary/20">
                  <td className="px-5 py-3 opacity-80">
                    {new Date(t.created_at).toLocaleString(undefined, {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    })}
                  </td>
                  <td className="px-5 py-3 capitalize">{t.type.replace('_', ' ')}</td>
                  <td className="px-5 py-3 tabular-nums font-medium">
                    {t.credits_usd >= 0 ? '+' : ''}${Math.abs(t.credits_usd).toFixed(2)}
                  </td>
                  <td className="px-5 py-3 tabular-nums opacity-70">
                    {t.charge_usd > 0 ? `$${t.charge_usd.toFixed(2)}` : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      t.status === 'completed'
                        ? 'bg-emerald-500/10 text-emerald-600'
                        : t.status === 'pending'
                          ? 'bg-amber-500/10 text-amber-600'
                          : 'bg-red-500/10 text-red-600'
                    }`}>
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
